import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../components/ui/button';
import { MapPinOff } from 'lucide-react';
import { MapLocation } from './types';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function FitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 6 });
    }
  }, [locations, map]);
  return null;
}

/**
 * CARTO basemaps need a key: without one every tile is stamped "API KEY
 * REQUIRED". The key is set per instance as the `REACT_APP_CARTO_BASEMAPS_KEY`
 * Heroku config var (baked in at build); local dev reads it from
 * `frontend/.env.local`.
 *
 * CR-8603: with no key the map is not drawn at all — the stamped tiles are
 * useless — and the card says so instead. CARTO answers a missing or wrong key
 * with an HTTP 200 stamp image, so the page cannot detect a wrong key; only a
 * missing key, or tiles that fail outright, can be caught here.
 */
function cartoTileUrl(): string | null {
  const key = process.env['REACT_APP_CARTO_BASEMAPS_KEY'];
  return key
    ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png' +
      `?key=${encodeURIComponent(key)}`
    : null;
}

const MAP_UNAVAILABLE_TITLE = 'Map unavailable';
const MAP_UNAVAILABLE_TEXT =
  "The supplier map can't be shown right now. The supplier list below isn't affected.";

/** CR-8603: the card's content when the map cannot load. */
function MapUnavailable() {
  return (
    <div
      role="status"
      className="h-full flex flex-col items-center justify-center gap-2 p-6 text-center bg-muted/40"
    >
      <MapPinOff className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <p className="font-medium">{MAP_UNAVAILABLE_TITLE}</p>
      <p className="text-sm text-muted-foreground max-w-md">{MAP_UNAVAILABLE_TEXT}</p>
    </div>
  );
}

interface SupplierMapProps {
  locations: MapLocation[];
  onViewDetails: (id: number) => void;
}

export function SupplierMap({ locations, onViewDetails }: SupplierMapProps) {
  // CR-8603: tiles that fail before any has loaded mean the map service can't
  // be reached (down, blocked, rate-limited). A stray failure after the map
  // has drawn leaves it in place.
  const [tilesFailed, setTilesFailed] = useState(false);
  const tileLoaded = useRef(false);
  const tileUrl = cartoTileUrl();

  if (!tileUrl || tilesFailed) {
    return (
      <div className="h-64 rounded-lg overflow-hidden border relative z-0" data-tour="supplier-map">
        <MapUnavailable />
      </div>
    );
  }

  return (
    <div className="h-64 rounded-lg overflow-hidden border relative z-0" data-tour="supplier-map">
      <MapContainer
        center={[30, 10]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <FitBounds locations={locations} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          url={tileUrl}
          eventHandlers={{
            tileload: () => {
              tileLoaded.current = true;
            },
            tileerror: () => {
              if (!tileLoaded.current) setTilesFailed(true);
            },
          }}
        />
        {locations.map((location) => (
          <Marker key={location.id} position={[location.lat, location.lng]}>
            <Popup>
              <div className="font-medium">{location.name}</div>
              <Button
                size="sm"
                variant="link"
                className="p-0 h-auto"
                onClick={() => onViewDetails(location.id)}
              >
                View Details
              </Button>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
